import { useMemo, useEffect, useCallback } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ROUTES, TOAST_AUTO_CLOSE_TIME } from '@shared/constants';
import { BackButton } from '@components/BackButton';
import SectionLoading from '@components/SectionLoading';
import { Sidebar } from './components/sidebar';
import { Container, Main, LoadingOverlay } from './styles';
import { fetchUserPage, showToast as showToastAction } from '../actions';
import { USER_PAGE_TYPES } from '../consts';
import { showToast as showToastHelper } from '../helpers';
import UserActivityLogV2 from './components/UserActivityLogV2';
import { UserInformation } from './components/UserInformation';
import { UserPersonalProgram } from './components/UserPersonalProgram/index.tsx';
import MentorTimeTracking from './components/MentorTimeTracking';
import MentorForms from './components/MentorForms';

/**
 * Wrapper component for all internal pages
 * Mounting: fetch user data by id
 */
export const UserPageWrapper = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { push } = useHistory();
  const { id } = useParams();
  const { t } = useTranslation();

  const { loading, error, toastType } = useSelector((state) => state.get('userPage').toJS());

  // pathname is /users/:id/:pageType(/:formActivityId), pageType is always the segment right after the user id
  const currentPageType = useMemo(() => pathname.split('/')[3], [pathname]);

  /**
   * Back to users list page
   */
  const goBackToUsers = () => {
    push(ROUTES.USERS.LIST);
  };

  useEffect(() => {
    if (error) {
      toast.error(t('Informative.Negative.usersNotLoad'), {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
    }
  }, [error]);

  useEffect(() => {
    if (toastType) {
      showToastHelper(toastType, t);
      dispatch(showToastAction(null));
    }
  }, [toastType, dispatch, t]);

  useEffect(() => {
    dispatch(fetchUserPage(id));
  }, []);

  /**
   * Render user page by type
   */
  const renderComponent = useCallback(() => {
    switch (currentPageType) {
      case USER_PAGE_TYPES.INFORMATION: return <UserInformation />;
      case USER_PAGE_TYPES.PERSONAL_PROGRAM: return <UserPersonalProgram />;
      case USER_PAGE_TYPES.LOG: return <UserActivityLogV2 />;
      case USER_PAGE_TYPES.TIME_TRACKING: return <MentorTimeTracking />;
      case USER_PAGE_TYPES.MENTOR_FORMS: return <MentorForms />;
      default: return null;
    }
  }, [currentPageType]);

  return (
    <Container>
      {loading && <LoadingOverlay><SectionLoading /></LoadingOverlay>}
      <Sidebar pageType={currentPageType} />
      <Main>
        <BackButton
          text={t('Actions.Navigation.backToUsers')}
          onClick={goBackToUsers}
          margin="0 0 15px 0"
        />
        {renderComponent()}
      </Main>
    </Container>
  );
};
